// Lightweight cadence picker → RRULE string converters. We only emit
// the small subset the backend whitelists (FREQ, INTERVAL, BYDAY,
// COUNT, UNTIL); anything more exotic (BYMONTH/BYSETPOS) would need
// separate handling on both sides.

export const CADENCE_PRESETS = [
  { key: "none", label: "Doesn't repeat", label_da: "Gentages ikke", rrule: null },
  { key: "daily", label: "Every day", label_da: "Hver dag", rrule: "FREQ=DAILY" },
  { key: "weekdays", label: "Every weekday (Mon–Fri)", label_da: "Hverdage (man–fre)", rrule: "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR" },
  { key: "weekly", label: "Every week", label_da: "Hver uge", rrule: "FREQ=WEEKLY" },
  { key: "biweekly", label: "Every other week", label_da: "Hver anden uge", rrule: "FREQ=WEEKLY;INTERVAL=2" },
  { key: "monthly", label: "Every month", label_da: "Hver måned", rrule: "FREQ=MONTHLY" },
  { key: "yearly", label: "Every year", label_da: "Hvert år", rrule: "FREQ=YEARLY" },
];

// Locale-aware label helper for a preset. Falls through to the English
// label if a translation is missing.
export function cadencePresetLabel(preset, lang = "en") {
  if (!preset) return "";
  if (lang === "da" && preset.label_da) return preset.label_da;
  return preset.label;
}

export const WEEKDAY_TOKENS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
export const WEEKDAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

export function presetForRrule(rrule) {
  if (!rrule) return "none";
  const norm = rrule.trim().toUpperCase();
  for (const p of CADENCE_PRESETS) {
    if (p.rrule && p.rrule === norm) return p.key;
  }
  return "custom";
}

// Derive the {freq, interval, byday[], count, until} bits from an
// RRULE string. `until` is returned as a YYYY-MM-DD string when present
// (we store it as a date, not a datetime, since calendars repeat in
// whole-day terms).
export function parseRrule(rrule) {
  if (!rrule) return { freq: null, interval: 1, byday: [], count: null, until: null };
  const out = { freq: null, interval: 1, byday: [], count: null, until: null };
  for (const part of rrule.split(";")) {
    const [k, v] = part.split("=");
    if (k === "FREQ") out.freq = v;
    else if (k === "INTERVAL") out.interval = Number(v) || 1;
    else if (k === "BYDAY") out.byday = v ? v.split(",") : [];
    else if (k === "COUNT") out.count = Number(v) || null;
    else if (k === "UNTIL") {
      // UNTIL stored as YYYYMMDDTHHMMSSZ — surface as YYYY-MM-DD.
      const m = v.match(/^(\d{4})(\d{2})(\d{2})/);
      if (m) out.until = `${m[1]}-${m[2]}-${m[3]}`;
    }
  }
  return out;
}

export function buildRruleFromCustom({ freq = "WEEKLY", interval = 1, byday = [], count = null, until = null }) {
  const parts = [`FREQ=${freq}`];
  if (interval && interval !== 1) parts.push(`INTERVAL=${interval}`);
  if (byday.length) parts.push(`BYDAY=${byday.join(",")}`);
  if (count) parts.push(`COUNT=${count}`);
  if (until) {
    // Accept YYYY-MM-DD → encode as YYYYMMDDT235959Z (end of that day UTC)
    const m = until.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) parts.push(`UNTIL=${m[1]}${m[2]}${m[3]}T235959Z`);
  }
  return parts.join(";");
}

// Append/replace COUNT or UNTIL on an existing RRULE without changing
// FREQ/INTERVAL/BYDAY. Pass `{count: null, until: null}` to clear both.
export function withRruleEnd(rrule, { count = null, until = null }) {
  if (!rrule) return rrule;
  const { freq, interval, byday } = parseRrule(rrule);
  if (!freq) return rrule;
  return buildRruleFromCustom({ freq, interval, byday, count, until });
}

// Human-readable summary for rendering inside an event row /
// editor preview. Examples:
//   "Repeats every weekday"
//   "Repeats every Mon, Wed"
//   "Repeats every 2 weeks"
//   "Repeats yearly"
// Pass `lang` ("en" | "da") for a locale-aware string; defaults to en.
export function summarizeRrule(rrule, lang = "en") {
  if (!rrule) return "";
  const { freq, interval, byday, count, until } = parseRrule(rrule);
  if (!freq) return "";
  const isDa = lang === "da";
  const day = (t) => (isDa
    ? { MO: "man", TU: "tir", WE: "ons", TH: "tor", FR: "fre", SA: "lør", SU: "søn" }[t] || t
    : { MO: "Mon", TU: "Tue", WE: "Wed", TH: "Thu", FR: "Fri", SA: "Sat", SU: "Sun" }[t] || t);
  let core = "";
  if (freq === "DAILY") {
    core = interval > 1
      ? (isDa ? `Gentages hver ${interval}. dag` : `Repeats every ${interval} days`)
      : (isDa ? "Gentages dagligt" : "Repeats daily");
  } else if (freq === "WEEKLY") {
    if (byday.length === 5 && ["MO", "TU", "WE", "TH", "FR"].every((d) => byday.includes(d))) {
      core = isDa ? "Gentages på hverdage" : "Repeats every weekday";
    } else if (byday.length) {
      core = isDa
        ? `Gentages ${byday.map(day).join(", ")}`
        : `Repeats ${byday.map(day).join(", ")}`;
    } else {
      core = interval > 1
        ? (isDa ? `Gentages hver ${interval}. uge` : `Repeats every ${interval} weeks`)
        : (isDa ? "Gentages ugentligt" : "Repeats weekly");
    }
  } else if (freq === "MONTHLY") {
    core = interval > 1
      ? (isDa ? `Gentages hver ${interval}. måned` : `Repeats every ${interval} months`)
      : (isDa ? "Gentages månedligt" : "Repeats monthly");
  } else if (freq === "YEARLY") {
    core = isDa ? "Gentages årligt" : "Repeats yearly";
  } else {
    core = isDa ? "Gentages" : "Repeats";
  }
  // Tail with end-condition.
  if (count) return isDa ? `${core} · ${count} gange i alt` : `${core} · ${count} times total`;
  if (until) {
    const u = new Date(`${until}T00:00:00`);
    const label = u.toLocaleDateString(isDa ? "da-DK" : "en-US", { month: "short", day: "numeric", year: "numeric" });
    return isDa ? `${core} · indtil ${label}` : `${core} · until ${label}`;
  }
  return core;
}

// Helper for the editor: virtual instance ids look like
// `<masterId>::<iso>`. Returns true if `id` came from a recurring expansion.
export const isVirtualInstanceId = (id) => typeof id === "string" && id.includes("::");
export const masterIdOf = (id) =>
  isVirtualInstanceId(id) ? id.split("::", 1)[0] : id;
